import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Download, FileText } from 'lucide-react';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import { useTranslation } from '../hooks/useTranslation';
import { Language } from '../translations';

interface AudioPlayerProps {
  audioUrl: string;
  script: string;
  darkMode: boolean;
  language: Language;
}

export function AudioPlayer({ audioUrl, script, darkMode, language }: AudioPlayerProps) {
  const { t } = useTranslation(language);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      const audio = audioRef.current;
      audio.load();
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
        audio.play().catch(console.error);
        setIsPlaying(true);
      });
    }
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const time = (percentage / 100) * audioRef.current.duration;

    audioRef.current.currentTime = time;
    setProgress(percentage);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    if (!audioRef.current) return;
    
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(console.error);
    setIsPlaying(true);
  };

  const handleDownloadMP3 = async () => {
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      saveAs(blob, 'podcast.mp3');
    } catch (error) {
      console.error('Error downloading MP3:', error);
    }
  };

  const handleDownloadPDF = () => {
    try {
      const pdf = new jsPDF();
      const splitText = script.split('\n');
      let yPosition = 20;
      
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text('Podcast Transcript', 20, yPosition);
      
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(12);
      
      splitText.forEach((line) => {
        if (yPosition >= 280) {
          pdf.addPage();
          yPosition = 20;
        }
        
        if (line.toLowerCase().startsWith('tom:')) {
          pdf.setTextColor(245, 101, 11); // orange-500
        } else {
          pdf.setTextColor(0, 0, 0);
        }
        
        pdf.text(line, 20, yPosition += 10);
      });
      
      pdf.save('podcast-transcript.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  if (!audioUrl) return null;

  return (
    <div className="mt-6 space-y-6 animate-fade-in">
      <div className={`p-6 rounded-lg ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-orange-200'
      } border transition-colors duration-200`}>
        <h2 className="text-lg font-semibold mb-4 text-orange-500">
          {t('audioPlayer', 'ready')}
        </h2>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePlayPause}
              className="p-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white transition-colors"
            >
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
            <button
              onClick={handleRestart}
              className="p-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white transition-colors"
            >
              <RotateCcw size={24} />
            </button>
            <audio
              ref={audioRef}
              src={audioUrl}
              preload="metadata"
              className="hidden"
            />
            <div className="flex-1">
              <div
                ref={progressBarRef}
                onClick={handleProgressClick}
                className="relative h-2 rounded-full overflow-hidden bg-orange-100 dark:bg-orange-900/30 cursor-pointer"
              >
                <div
                  className="absolute h-full bg-orange-500 transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-1 text-sm text-orange-500">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleDownloadMP3}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Download size={20} />
              <span>{t('audioPlayer', 'downloadAudio')}</span>
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <FileText size={20} />
              <span>{t('audioPlayer', 'downloadTranscript')}</span>
            </button>
          </div>
        </div>
      </div>

      {script && (
        <div className={`p-6 rounded-lg ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-orange-200'
        } border transition-colors duration-200`}>
          <h3 className="text-lg font-semibold mb-4 text-orange-500">
            {t('audioPlayer', 'script')}
          </h3>
          <div className="prose dark:prose-invert max-w-none">
            {script.split('\n').map((line, index) => {
              const isTom = line.toLowerCase().startsWith('tom:');
              return (
                <p key={index} className={`mb-2 ${
                  isTom ? 'text-orange-500 font-semibold' : 
                  darkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  {line}
                </p>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}